import React, { useMemo } from "react";
import { get, set } from "lodash";
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";

import { formatInputWithPrefixAndSuffix } from "../../../utils/formattingUtils";
import { INITIAL_SALES_APPROACH_TABLE_DATA_DEFINITION } from "./salesApproachTableDefinition";
import { INITIAL_INCOME_APPROACH_TABLE_DATA_DEFINITION } from "./incomeApproachTableDefinition";
import { ApproachTableComponent } from "./ApproachTableComponent";
import { SpaceToggleComponent } from "../commons/SpaceToggleComponent";
import { ExpandedCompsSheet } from "../expanded-comps/ExpandedCompsSheet";
import { ApproachType } from "../../../endpoints/getExpandedComps";
import { useSheets } from "../../../features/sheets/hooks";
import Button from "@amzn/meridian/button";

export interface IAppealProps {
    data: any;
    handleUpdateData: (data: any) => void;
    viewType: "SALES" | "INCOME" | "COST";
    approachType?: "leaseAdjustment" | "incomeExpenseProForma" | "marketCapVacancyRentalRate";
    spaceId?: string;
    isEditing: boolean;
    spaceType?: string;
    handleInputChange?: (field: string, value: any) => void;
    refetch?: () => void;
}

export interface ISummaryTableProps {
    data: any;
    summaryPrefix: string;
    viewType: "SALES" | "INCOME" | "COST";
}

const SalesReviewTable: React.FC<ISummaryTableProps> = ({ data, summaryPrefix, viewType }) => {
    const getPath = (field: string) =>
        viewType === "SALES"
            ? `${summaryPrefix}.salesReviewIndication.${field}`
            : `${summaryPrefix}.leaseReviewIndication.${field}`;

    return (
        <Table showDividers={true} spacing={"small"}>
            <TableRow>
                <TableCell backgroundColor="secondary">
                    <Text type="b300">Average</Text>
                </TableCell>
                <TableCell alignmentHorizontal="end">
                    <Text type="h100">
                        {formatInputWithPrefixAndSuffix(get(data, getPath("average")), "$")}
                    </Text>
                </TableCell>
                <TableCell backgroundColor="secondary">
                    <Text type="b300">Low</Text>
                </TableCell>
                <TableCell alignmentHorizontal="end">
                    <Text type="h100">
                        {formatInputWithPrefixAndSuffix(get(data, getPath("low")), "$")}
                    </Text>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell backgroundColor="secondary">
                    <Text type="b300">Median</Text>
                </TableCell>
                <TableCell alignmentHorizontal="end">
                    <Text type="h100">
                        {formatInputWithPrefixAndSuffix(get(data, getPath("median")), "$")}
                    </Text>
                </TableCell>
                <TableCell backgroundColor="secondary">
                    <Text type="b300">High</Text>
                </TableCell>
                <TableCell alignmentHorizontal="end">
                    <Text type="h100">
                        {formatInputWithPrefixAndSuffix(get(data, getPath("high")), "$")}
                    </Text>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell backgroundColor="secondary">
                    <Text type="b300">Weighted Average</Text>
                </TableCell>
                <TableCell alignmentHorizontal="end">
                    <Text type="h100">
                        {formatInputWithPrefixAndSuffix(get(data, getPath("weightedAverage")), "$")}
                    </Text>
                </TableCell>
                <TableCell backgroundColor="secondary" />
                <TableCell />
            </TableRow>
        </Table>
    );
};

const IndicatedValueTable: React.FC<{ data: any; summaryPrefix: string }> = ({
    data,
    summaryPrefix,
}) => (
    <Table showDividers={true} spacing={"small"}>
        {[
            { label: "Indicated rate", field: "indicatedRate", suffix: "" },
            { label: "Square feet", field: "squareFeet", suffix: " sqft" },
            { label: "Indicated value", field: "indicatedValue", suffix: "" },
            { label: "Reconciled", field: "reconciled", suffix: "" },
        ].map(({ label, field, suffix }) => (
            <TableRow key={field}>
                <TableCell backgroundColor="secondary">
                    <Text type="b300">{label}</Text>
                </TableCell>
                <TableCell alignmentHorizontal="end">
                    <Text type="h100">
                        {formatInputWithPrefixAndSuffix(
                            get(data, `${summaryPrefix}.indicatedValue.${field}`),
                            suffix === "" ? "$" : "",
                            suffix
                        )}
                    </Text>
                </TableCell>
            </TableRow>
        ))}
    </Table>
);

export const ApproachComponent: React.FC<IAppealProps> = ({
    data,
    handleUpdateData,
    viewType,
    approachType,
    spaceId,
    isEditing,
    refetch,
}) => {
    const { pushToStack, setOpenSheets, closeSheetFromStack, getCurrentStack } = useSheets();

    const handleOpenSheet = () => {
        const currentStack = getCurrentStack();
        const isExpandedCompsOpen = currentStack.some(sheet => sheet.id === "expandedComps");

        if (!isExpandedCompsOpen) {
            const expandedCompsSheet = (
                <ExpandedCompsSheet
                    viewType={viewType as ApproachType}
                    data={data}
                    spaceId={spaceId}
                    closeSheet={() => closeSheetFromStack("expandedComps")}
                    refetch={refetch}
                />
            );
            const result = pushToStack(expandedCompsSheet, "1200px", "expandedComps");
            if (result) {
                setOpenSheets(true);
            }
        } else {
            setOpenSheets(true);
        }
    };

    const summaryPrefix = useMemo(
        () =>
            viewType === "SALES"
                ? "appealDetails.appealApproachCalculationData.sales.summary"
                : `appealDetails.appealApproachCalculationData.income.spaces.${spaceId}.${approachType}.summary`,
        [viewType, spaceId, data, approachType]
    );

    const approachTableMapping = useMemo(
        () =>
            viewType === "SALES"
                ? { ...INITIAL_SALES_APPROACH_TABLE_DATA_DEFINITION(data) }
                : { ...INITIAL_INCOME_APPROACH_TABLE_DATA_DEFINITION(data, spaceId) },
        [viewType, data, spaceId]
    );

    const handleInputChange = (field: string, value: any) => {
        const updatedData = JSON.parse(JSON.stringify(data));
        set(updatedData, field, value);
        handleUpdateData(updatedData);
    };

    return (
        <>
            {viewType !== "SALES" && (
                <SpaceToggleComponent
                    data={data}
                    isEditing={isEditing}
                    spaceId={spaceId}
                    handleInputChange={handleInputChange}
                />
            )}

            <Box>
                <Box height="20px" />
                <Row alignmentHorizontal={"justify"}>
                    <Text type="h400">Improved/Equity sales adjustment grid</Text>
                    <Button onClick={handleOpenSheet} type="tertiary" size={"small"}>
                        Edit Comps
                    </Button>
                </Row>
                <Box height="20px" />
                <Row
                    width="100%"
                    widths={["60%", "40%"]}
                    backgroundColor="subdued"
                    alignmentVertical="top"
                >
                    <Box spacingInset="500 400 500 500">
                        <Text type="h200">
                            {viewType === "SALES" ? "Sales" : "Lease"} review indication
                        </Text>
                        <Box height="10px" />
                        <SalesReviewTable
                            data={data}
                            summaryPrefix={summaryPrefix}
                            viewType={viewType}
                        />
                    </Box>
                    <Box spacingInset="500 500 500 0">
                        <Text type="h200">Indicated value</Text>
                        <Box height="10px" />
                        <IndicatedValueTable data={data} summaryPrefix={summaryPrefix} />
                    </Box>
                </Row>
            </Box>

            <Box height="30px" />
            <ApproachTableComponent
                isEditing={isEditing}
                tableMapping={approachTableMapping}
                data={data}
                handleUpdateData={handleUpdateData}
            />
        </>
    );
};
